import { BufferDurations } from '../../user-dashboard/shared/buffer-durations.model';
import { FeatureCompatibility } from '../../shared/feature-compatibility';
import { Region } from '../../user-dashboard/shared/region.model';
import { Moment } from 'moment';
import { SapPlantFeatureFlags } from './sap-plant-feature-flags.model';
import { HaulierScac } from './haulier-scac';
import { CustomerGlid } from './customer-glid.model';
import { CmaLiteCarriersConfiguration } from '../../shared/cma-lite-carriers-configuration';
import { CmaLiteDeliveriesConfiguration } from '../../shared/cma-lite-deliveries-configuration';
import { OnOffFeature } from '../../user-dashboard/shared/on-off-feature.model';

export class LocalLocationContainerConfiguration {

  public defaultLang: string;
  public defaultCmblLang: string;
  public ymsCompatible: FeatureCompatibility;
  public additionnalHaulierAllowed: boolean;
  public region: Region;
  public overriddenBufferDuration: BufferDurations;
  public safetyIcons?: Array<string>;
  public googleTimezone: string;
  public googleTimezoneResolvedOn: Moment;
  public sapTimezone: string;
  public sapTimezoneResolvedOn: Moment;
  public dhHaulierScacs: string[];
  public dhCustomerGlids: string[];
  public uaHaulierScacs: string[];
  public sapFeatureFlags: SapPlantFeatureFlags;
  public colt: boolean;
  public enableForceCheckin: boolean;
  public forcedCheckinMaxRadius: number;
  public localizedDeclarationTexts: { [lang: string]: string };
  public cmaLiteActivated: boolean;
  public cmaLiteCarriersConfiguration: CmaLiteCarriersConfiguration;
  public cmaLiteDeliveriesConfiguration: CmaLiteDeliveriesConfiguration;
  public cicoMobileEnabled: boolean;
  public askZ2EtaCico: boolean;
  public z1CheckoutToMyChep: boolean;
  public cmaCicoAlignment: boolean;
  public documentDownloadQrCodeFeature: OnOffFeature;

  static fromDto(data: any): LocalLocationContainerConfiguration {
    const configuration = new LocalLocationContainerConfiguration();

    if (!data) {
      return null;
    }

    configuration.defaultLang                    = data.default_lang;
    configuration.defaultCmblLang                = data.default_cmbl_lang;
    configuration.ymsCompatible                  = FeatureCompatibility.fromValue(data.yms_compatible);
    configuration.additionnalHaulierAllowed      = data.additionnal_haulier_allowed;
    configuration.region                         = Region.fromDto(data.region);
    configuration.overriddenBufferDuration       = BufferDurations.fromDto(data.overridden_buffer_durations);
    configuration.googleTimezone                 = data.google_timezone;
    configuration.googleTimezoneResolvedOn       = data.google_timezone_resolved_on;
    configuration.sapTimezone                    = data.sap_timezone;
    configuration.sapTimezoneResolvedOn          = data.sap_timezone_resolved_on;
    configuration.colt                           = data.colt;
    configuration.sapFeatureFlags                = SapPlantFeatureFlags.fromDto(data.sap_feature_flags);
    configuration.enableForceCheckin             = data.enable_force_checkin;
    configuration.forcedCheckinMaxRadius         = data.forced_checkin_max_radius;
    configuration.localizedDeclarationTexts      = data.localized_declaration_texts;
    configuration.cmaLiteActivated               = data.cma_lite_activated;
    configuration.cmaLiteCarriersConfiguration   = data.cma_lite_carriers_configuration;
    configuration.cmaLiteDeliveriesConfiguration = data.cma_lite_deliveries_configuration;
    configuration.cicoMobileEnabled              = data.cico_mobile_enabled;
    configuration.askZ2EtaCico                   = data.ask_z2_eta_cico;
    configuration.z1CheckoutToMyChep             = data.z1_checkout_to_my_chep;
    configuration.cmaCicoAlignment               = data.cma_cico_alignment;
    configuration.documentDownloadQrCodeFeature  = data.document_download_qr_code_feature

    if (data.dh_haulier_scacs) {
      configuration.dhHaulierScacs = data.dh_haulier_scacs.map(hs => HaulierScac.fromDto(hs))
        .map(hs => hs.scacCode);
    }
    if (data.dh_customer_glids) {
      configuration.dhCustomerGlids = data.dh_customer_glids.map(hs => CustomerGlid.fromDto(hs))
        .map(cg => cg.glid);
    }
    if (data.plant_hauliers && data.plant_hauliers.length > 0) {
      configuration.uaHaulierScacs = data.plant_hauliers
        .filter(h => h.ua_multi_glids)
        .map(h => h.scac_code);
    }

    if (data.safety_icons) {
      configuration.safetyIcons = data.safety_icons.filter(icon => icon !== null);
    }

    return configuration;
  }

  static toDto(c: LocalLocationContainerConfiguration): any {
    if (!c) {
      return null;
    }

    return {
      default_lang: c.defaultLang,
      yms_compatible: c.ymsCompatible.value,
      region: Region.toDto(c.region),
      overridden_buffer_durations: BufferDurations.toDto(c.overriddenBufferDuration),
      google_timezone: c.googleTimezone,
      google_timezone_resolved_on: c.googleTimezoneResolvedOn,
      sap_timezone: c.sapTimezone,
      sap_timezone_resolved_on: c.sapTimezoneResolvedOn,
      colt: c.colt,
      enable_force_checkin: c.enableForceCheckin,
      forced_checkin_max_radius: c.forcedCheckinMaxRadius,
      cma_lite_activated: c.cmaLiteActivated,
      cma_lite_carriers_configuration: c.cmaLiteCarriersConfiguration,
      cma_lite_deliveries_configuration: c.cmaLiteDeliveriesConfiguration,
      cico_mobile_enabled: c.cicoMobileEnabled,
      ask_z2_eta_cico : c.askZ2EtaCico,
      z1_checkout_to_my_chep : c.z1CheckoutToMyChep,
      cma_cico_alignment : c.cmaCicoAlignment,
      document_download_qr_code_feature : c.documentDownloadQrCodeFeature
    };
  }

  constructor(region?: Region, overriddenBufferDuration?: BufferDurations, enableForceCheckin?: boolean, forcedCheckinMaxRadius?: number, cicoMobile?: boolean) {
    this.region = region;
    this.overriddenBufferDuration = overriddenBufferDuration;
    this.enableForceCheckin = enableForceCheckin;
    this.forcedCheckinMaxRadius = forcedCheckinMaxRadius;
    this.cicoMobileEnabled = cicoMobile;
  }

}
